import * as React from "react";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Paper from "@mui/material/Paper";
import Grid from "@mui/material/Grid2";
import {
  Alert,
  Autocomplete,
  Button,
  CircularProgress,
  Stack,
  TableSortLabel,
  TextField,
  Typography,
} from "@mui/material";
import { Box } from "@mui/system";
import { useSelector } from "react-redux";
import { useNavigate, useSearchParams } from "react-router-dom";
import AddIcon from "@mui/icons-material/Add";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import AdvisorMapFormSkeleton from "../AdvisorMapFormSkeleton";
import { selectUserToken } from "../../../../features/userSlice";
import PersistentErrorSnackbar from "../../../PersistentErrorSnackbar/PersistentErrorSnackbar";
import { AdvisorMapsEndpointLogic } from "../../../../logic/DataCollection/Advisor/AdvisorMapsEndPointLogic";
import {
  ADVISOR_GAP_RULE_NOTE,
  ADVISOR_GAP_RULE_PLACEHOLDER,
  AdvisorGapRule,
  advisorCanonicalMetrics,
} from "../helpers/advisorStaticData";
import "../Advisor.css";

type AdvisorOption = { id: number; label: string };
type SortDirection = "asc" | "desc";

const tableHeaders = [
  { id: 0, KEY: "metric", label: "Metric", sortable: true },
  { id: 1, KEY: "gapRule", label: "Gap Rule", sortable: true },
  { id: 2, KEY: "derivations", label: "Derivations", sortable: true },
  { id: 3, KEY: "dependsOn", label: "Depends On", sortable: true },
];

const AdvisorDuplicateMap = () => {
  const oauthTokenHeader: any = useSelector(selectUserToken);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const interfaceData = useSelector(
    (state: any) => state.advisor?.interfaceData,
  );
  const destinationTablesData = useSelector(
    (state: any) => state.advisor?.destinationTablesData,
  );
  const gapRulesData: AdvisorGapRule[] = useSelector(
    (state: any) => state.advisor?.gapRulesData,
  );
  const advisorMapsData = useSelector(
    (state: any) => state.advisor?.businessAdvisorMapsData,
  );

  const mapIdFromUrl = searchParams.get("mapId");
  const interfaceIDFromUrl = searchParams.get("interfaceID");
  // the list's filter, not this map's own table - the maps are fetched for the
  // same query the list used, so the store stays valid for it on the way back
  const destinationTableIDFromUrl = searchParams.get("destinationTableIDMain");

  const [loading, setLoading] = React.useState(true);
  const [submitting, setSubmitting] = React.useState(false);
  /** seeded as "<source name>_copy" once the map loads, and editable from there */
  const [mapName, setMapName] = React.useState("");
  const [selectedGapRules, setSelectedGapRules] = React.useState<
    Record<string, AdvisorGapRule | null>
  >({});
  const [sort, setSort] = React.useState<{
    keytoSort: string;
    direction: SortDirection;
  }>({ keytoSort: "", direction: "asc" });

  // Everything this screen shows is re-fetched on each visit: the dropdown
  // options plus the source map, looked up from the list for the interface and
  // destination table named in the URL.
  React.useEffect(() => {
    const fetchMap = async () => {
      setLoading(true);
      try {
        await Promise.all([
          AdvisorMapsEndpointLogic.getInterface(oauthTokenHeader),
          AdvisorMapsEndpointLogic.getDestinationTables(oauthTokenHeader),
          AdvisorMapsEndpointLogic.getGapRules(oauthTokenHeader),
          AdvisorMapsEndpointLogic.getBusinessAdvisorMaps(
            oauthTokenHeader,
            interfaceIDFromUrl,
            destinationTableIDFromUrl,
          ),
        ]);
      } finally {
        setLoading(false);
      }
    };
    fetchMap();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mapIdFromUrl, interfaceIDFromUrl, destinationTableIDFromUrl]);

  const mapToDuplicate: any = React.useMemo(
    () =>
      (advisorMapsData ?? []).find(
        (row: any) => String(row?.id) === mapIdFromUrl,
      ) ?? null,
    [advisorMapsData, mapIdFromUrl],
  );

  // Suggest a name for the copy. Kept off the gap rules so a late arrival of
  // those cannot wipe out a name the user has already started editing.
  React.useEffect(() => {
    if (!mapToDuplicate?.name) return;
    setMapName(`${mapToDuplicate.name}_copy`);
  }, [mapToDuplicate]);

  // Carry the source map's assignments over once it lands, and again if the gap
  // rules arrive after it so they can be resolved.
  React.useEffect(() => {
    if (!mapToDuplicate) return;
    setSelectedGapRules(
      advisorCanonicalMetrics.reduce<Record<string, AdvisorGapRule | null>>(
        (accumulator, metric) => {
          // 0 (or a missing rule) means the metric comes straight from the source
          const assignedRuleId = mapToDuplicate[metric]?.id;
          accumulator[metric] = assignedRuleId
            ? ((gapRulesData ?? []).find(
                (rule) => rule.id === assignedRuleId,
              ) ?? null)
            : null;
          return accumulator;
        },
        {},
      ),
    );
  }, [mapToDuplicate, gapRulesData]);

  const interfaceOptions: AdvisorOption[] = React.useMemo(
    () =>
      interfaceData?.map((item: any) => ({
        label: item?.affiliate_system_interface_name,
        id: item?.id,
      })) || [],
    [interfaceData],
  );

  const destinationTableOptions: AdvisorOption[] = React.useMemo(
    () =>
      destinationTablesData?.map((item: any) => ({
        label: item?.destination_table,
        id: item?.id,
      })) || [],
    [destinationTablesData],
  );

  // the copy keeps the source map's interface and destination table
  const selectedInterface =
    interfaceOptions.find(
      (option) => option.id === mapToDuplicate?.backend_interface_id,
    ) ?? null;
  const selectedDestinationTable =
    destinationTableOptions.find(
      (option) => option.id === mapToDuplicate?.destination_table_id,
    ) ?? null;

  const isValidPayload = Boolean(mapName.trim() && mapToDuplicate);

  /**
   * Rules are narrowed to the map's destination table and to the metric they
   * target - the destination is inherited here, so it comes off the map.
   */
  const getGapRuleOptions = React.useCallback(
    (metric: string) =>
      mapToDuplicate
        ? (gapRulesData ?? []).filter(
            (rule) =>
              rule.destination_table_id ===
                mapToDuplicate.destination_table_id &&
              rule.target_field === metric,
          )
        : [],
    [gapRulesData, mapToDuplicate],
  );

  const handleSelectGapRule = (
    metric: string,
    gapRule: AdvisorGapRule | null,
  ) => {
    setSelectedGapRules((prev) => ({ ...prev, [metric]: gapRule }));
  };

  const handleCreateMap = async () => {
    if (!isValidPayload || !mapToDuplicate) return;
    // every canonical metric is sent - 0 means "no gap rule, use the source"
    const assignments = advisorCanonicalMetrics.reduce<Record<string, number>>(
      (accumulator, metric) => {
        accumulator[metric] = selectedGapRules[metric]?.id ?? 0;
        return accumulator;
      },
      {},
    );

    setSubmitting(true);
    try {
      // a duplicate is just a new map, so it goes through the create endpoint
      const isSuccess = await AdvisorMapsEndpointLogic.createAdvisorMap(
        oauthTokenHeader,
        {
          name: mapName.trim(),
          backend_interface_id: mapToDuplicate.backend_interface_id,
          destination_table_id: mapToDuplicate.destination_table_id,
          assignments,
        },
      );
      if (isSuccess) {
        const currentParams = searchParams.toString();
        navigate(
          `/data_collection/advisor${currentParams ? `?${currentParams}` : ""}`,
          { replace: true },
        );
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleHeaderClick = (header: (typeof tableHeaders)[number]) => {
    if (!header.sortable) return;
    setSort((prev) => ({
      keytoSort: header.KEY,
      direction:
        prev.keytoSort === header.KEY && prev.direction === "asc"
          ? "desc"
          : "asc",
    }));
  };

  const sortedMetrics = React.useMemo(() => {
    if (!sort.keytoSort) return advisorCanonicalMetrics;

    const getSortValue = (metric: string) => {
      const rule = selectedGapRules[metric];
      if (sort.keytoSort === "gapRule") return rule ? String(rule.id) : "";
      if (sort.keytoSort === "derivations") return rule?.rule_title ?? "";
      if (sort.keytoSort === "dependsOn") return rule?.dependency_field ?? "";
      return metric;
    };
    return advisorCanonicalMetrics.slice().sort((a, b) => {
      const aValue = getSortValue(a);
      const bValue = getSortValue(b);
      // metrics without a gap rule always sink to the bottom
      if (!aValue) return 1;
      if (!bValue) return -1;
      return sort.direction === "asc"
        ? aValue.localeCompare(bValue)
        : bValue.localeCompare(aValue);
    });
  }, [sort, selectedGapRules]);

  /** how many metrics this destination can derive with a rule instead of the source */
  // nothing on this page is meaningful until all four requests are in
  if (loading) {
    return <AdvisorMapFormSkeleton />;
  }

  const derivableCount = advisorCanonicalMetrics.filter(
    (metric) => getGapRuleOptions(metric).length > 0,
  ).length;

  return (
    <Paper sx={{ mt: 2 }}>
      <PersistentErrorSnackbar />
      <Box sx={{ padding: "8px 16px" }}>
        <Typography variant="h3" sx={{ fontSize: "24px", mb: 2 }}>
          Duplicate Map
        </Typography>

        <Grid container spacing={2} alignItems="flex-start" sx={{ mb: 1 }}>
          <Grid size={{ xs: 12, md: 9 }}>
            <Stack direction="row" alignItems="flex-start" gap={2}>
              <TextField
                sx={{ width: "100%" }}
                label="Map Name"
                placeholder="Please enter map name"
                helperText="Mandatory Field"
                required
                color="secondary"
                size="small"
                value={mapName}
                disabled={loading}
                onChange={(event) => setMapName(event.target.value)}
              />
              {/* the copy inherits both from the source map - not editable */}
              <Autocomplete
                sx={{ width: "100%" }}
                options={interfaceOptions}
                value={selectedInterface}
                disabled
                isOptionEqualToValue={(option, value) => option.id === value.id}
                onChange={() => {}}
                renderInput={(params) => (
                  <TextField {...params} label="Interface" color="secondary" />
                )}
                size="small"
              />
              <Autocomplete
                sx={{ width: "100%" }}
                options={destinationTableOptions}
                value={selectedDestinationTable}
                disabled
                isOptionEqualToValue={(option, value) => option.id === value.id}
                onChange={() => {}}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Destination Table"
                    color="secondary"
                  />
                )}
                size="small"
              />
            </Stack>
          </Grid>
          <Grid
            size={{ xs: 12, md: 3 }}
            container
            direction="row"
            alignItems="center"
            justifyContent="flex-end"
          >
            <Button
              variant="contained"
              color="secondary"
              disabled={!isValidPayload || loading || submitting}
              onClick={handleCreateMap}
              data-tracker="949"
              sx={{ whiteSpace: "nowrap" }}
            >
              Create MAP
              {submitting ? (
                <CircularProgress
                  size={16}
                  color="inherit"
                  sx={{ ml: "8px" }}
                />
              ) : (
                <AddIcon sx={{ width: "20px", ml: "5px" }} />
              )}
            </Button>
          </Grid>
        </Grid>

        <Alert
          severity="warning"
          icon={<ErrorOutlineIcon fontSize="small" />}
          sx={{
            mb: 1,
            color: "warning.main",
            "& .MuiAlert-icon": { color: "warning.main", alignItems: "center" },
            "& .MuiAlert-message": { fontWeight: 500, fontSize: "14px" },
          }}
        >
          {ADVISOR_GAP_RULE_NOTE}
        </Alert>
      </Box>

      <TableContainer className="advisorDuplicateMapTable">
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              {tableHeaders.map((header) => (
                <TableCell key={header.id} align="left">
                  <TableSortLabel
                    active={sort.keytoSort === header.KEY}
                    direction={
                      sort.keytoSort === header.KEY ? sort.direction : "asc"
                    }
                    onClick={() => handleHeaderClick(header)}
                    disabled={!header.sortable}
                  >
                    {header.label}
                  </TableSortLabel>
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {sortedMetrics.map((metric) => {
              const selectedRule = selectedGapRules[metric] || null;

              return (
                <TableRow
                  key={metric}
                  sx={{
                    "& > *": {
                      borderBottom:
                        "1px solid rgba(224, 224, 224, 1) !important",
                      padding: "10px 16px",
                    },
                  }}
                >
                  <TableCell align="left">{metric}</TableCell>
                  <TableCell align="left">
                    <Autocomplete
                      sx={{ width: "210px" }}
                      options={getGapRuleOptions(metric)}
                      getOptionLabel={(option) => option.id.toString()}
                      value={selectedRule}
                      loading={loading}
                      disabled={loading || !mapToDuplicate}
                      isOptionEqualToValue={(option, value) =>
                        option.id === value.id
                      }
                      onChange={(_, newValue) =>
                        handleSelectGapRule(metric, newValue)
                      }
                      noOptionsText="No gap rule for this metric"
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          placeholder={ADVISOR_GAP_RULE_PLACEHOLDER}
                          color="secondary"
                        />
                      )}
                      size="small"
                    />
                  </TableCell>
                  <TableCell align="left">
                    {selectedRule ? (
                      <Typography component="span" sx={{ fontSize: "14px" }}>
                        {selectedRule.rule_title}
                      </Typography>
                    ) : (
                      <Typography
                        component="span"
                        sx={{ fontSize: "14px", color: "text.secondary" }}
                      >
                        from source
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell align="left">
                    {/* dependency_field arrives comma separated, no spaces */}
                    {selectedRule?.dependency_field
                      ? selectedRule.dependency_field.split(",").join(", ")
                      : "-"}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      <Box sx={{ padding: "8px 16px" }}>
        <Typography sx={{ fontSize: "12px", color: "text.secondary" }}>
          {`${derivableCount} of ${advisorCanonicalMetrics.length} metrics derived by a rule`}
        </Typography>
      </Box>
    </Paper>
  );
};

export default AdvisorDuplicateMap;
